import { Component, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AngularSvgIconModule } from "angular-svg-icon";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { TableModule } from "primeng/table";
import { ApeButtonComponent } from "../ape-button/ape-button.component";
import { ApeStatusBadgeComponent, StatusBadgeKind } from "../ape-status-badge/ape-status-badge.component";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { ApeAvatarComponent } from "../ape-avatar/ape-avatar.component";
import { LoggerService } from "src/ancestors/logger.service";
import { ApeMenuComponent } from "../ape-menu/ape-menu.component";
import { MenuItem } from "primeng/api";
import { Menu } from "primeng/menu";
import { TranslateService } from "src/app/util/translate.service";
import { MonitoringUserDevelopmentPlanSummaryExtended } from "src/app/services/api/monitoring-backoffice-api.service";
import { DevelopmentPlanPhaseManagerStatusEnum, DevelopmentPlanPhaseManagerStatusEnumTypes, DevelopmentPlanTeamMemberStatusEnum, DevelopmentPlanTeamMemberStatusEnumTypes, ProcessStatusEnum, ProcessStatusEnumTypes, TalentMatrixBoxEnum } from "alloy-performance-engine-cl";
import { UrlCollection } from "src/app/constants/url-collection";
import { Router } from "@angular/router";
import { getQuadrantDataByEnum } from "../../constants/talent-matrix-quadrants";

@Component({
  selector: "ape-admin-monitoring-users-development-plan-table",
  templateUrl: "./ape-admin-monitoring-users-development-plan-table.component.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    TranslatePipe,
    TableModule,
    ApeButtonComponent,
    ApeStatusBadgeComponent,
    ApeAvatarComponent,
    ApeMenuComponent
  ]
})
export class ApeAdminMonitoringUsersDevelopmentPlanTableComponent {
  /**
   * Chiavi di traduzione per type safety
   */
  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  private logger = inject(LoggerService);

  private router = inject(Router);

  @Input() usersData: MonitoringUserDevelopmentPlanSummaryExtended[] = [];

  @Input() isPhaseActive: boolean = false;

  @Input() processStatus: ProcessStatusEnum | null = null;

  /**
   * Riferimento al menu tiered
   */
  @ViewChild("apeMenu") apeMenu!: Menu;

  /**
   * Menu items per le azioni
   */
  menuItems: MenuItem[] = [];

  @Input() processId: string | null = null;

  @Output() onReopenDevelopmentPlan = new EventEmitter<MonitoringUserDevelopmentPlanSummaryExtended>();

  @Output() onSolicitDevelopmentPlan = new EventEmitter<MonitoringUserDevelopmentPlanSummaryExtended>();

  @Output() impersonateUser = new EventEmitter<MonitoringUserDevelopmentPlanSummaryExtended>();

  @Output() impersonateManager = new EventEmitter<MonitoringUserDevelopmentPlanSummaryExtended>();

  private translateService = inject(TranslateService);

  protected getPerformanceValueString(talentBoxMatrix: TalentMatrixBoxEnum): string | undefined {
    if (!talentBoxMatrix) {
      return undefined;
    }

    // Fascia ed etichetta si leggono dal quadrante: prima c'era uno `switch` a nove casi che
    // NON rompeva la compilazione con l'enum a 15 valori e sarebbe rimasto indietro in silenzio,
    // restituendo `undefined` per i sei quadranti nuovi.
    const quadrant = getQuadrantDataByEnum(talentBoxMatrix);
    if (!quadrant) {
      return undefined;
    }
    return `${this.translateService.instant<string>(quadrant.bandKey)} - ${this.translateService.instant<string>(quadrant.labelKey)}`;
  }

  protected getMandatoryStatusKind(status: boolean): StatusBadgeKind {
    return status
      ? "to-start"
      : "disable";
  }

  protected getMandatoryString(status: boolean): string {
    return status
      ? this.translateService.instant(TRANSLATION_KEYS.generic.YES)
      : this.translateService.instant(TRANSLATION_KEYS.generic.NO);
  }

  protected getDevelopmentPlanStatusKind(status: DevelopmentPlanTeamMemberStatusEnum | DevelopmentPlanPhaseManagerStatusEnum, isMandatory: boolean): StatusBadgeKind {
    if (isMandatory) {
      switch (status) {
        case DevelopmentPlanTeamMemberStatusEnumTypes.IN_PROGRESS:
        case DevelopmentPlanPhaseManagerStatusEnumTypes.STARTED:
          return "in-progress";
        case DevelopmentPlanTeamMemberStatusEnumTypes.COMPLETED:
          return "completed";
        default:
          return "in-progress";
      }
    } else {
      switch (status) {
        case DevelopmentPlanTeamMemberStatusEnumTypes.IN_PROGRESS:
        case DevelopmentPlanPhaseManagerStatusEnumTypes.STARTED:
          return "in-progress";
        case DevelopmentPlanTeamMemberStatusEnumTypes.COMPLETED:
          return "completed";
        default:
          return "disable";
      }
    }
  }

  protected getDevelopmentPlanStausLabel(status: DevelopmentPlanTeamMemberStatusEnum | DevelopmentPlanPhaseManagerStatusEnum, isMandatory: boolean): string {
    if (isMandatory) {
      switch (status) {
        case DevelopmentPlanTeamMemberStatusEnumTypes.IN_PROGRESS:
        case DevelopmentPlanPhaseManagerStatusEnumTypes.STARTED:
        case DevelopmentPlanPhaseManagerStatusEnumTypes.TO_START:
          return this.translateService.instant(TRANSLATION_KEYS.generic.developmentPlan.status.WAITING);
        case DevelopmentPlanTeamMemberStatusEnumTypes.COMPLETED:
          return this.translateService.instant(TRANSLATION_KEYS.generic.developmentPlan.status.COMPLETED);
        default:
          return this.translateService.instant(TRANSLATION_KEYS.generic.developmentPlan.status.WAITING);
      }
    } else {
      switch (status) {
        case DevelopmentPlanTeamMemberStatusEnumTypes.IN_PROGRESS:
        case DevelopmentPlanPhaseManagerStatusEnumTypes.STARTED:
          return this.translateService.instant(TRANSLATION_KEYS.generic.developmentPlan.status.WAITING);
        case DevelopmentPlanTeamMemberStatusEnumTypes.COMPLETED:
          return this.translateService.instant(TRANSLATION_KEYS.generic.developmentPlan.status.COMPLETED);
        default:
          return this.translateService.instant(TRANSLATION_KEYS.generic.developmentPlan.status.OPTIONAL);
      }
    }
  }

  protected reopenDevelopmentPlan(user: MonitoringUserDevelopmentPlanSummaryExtended) {
    if (!user) return;
    if (!user.canReopenDevelopmentPlan) return;
    this.logger.info(`Reopen evaluation for user ${user.userId}`);
    this.onReopenDevelopmentPlan.emit(user);
  }

  private canSolicitDevelopmentPlan(user: MonitoringUserDevelopmentPlanSummaryExtended) {
    if (!this.isPhaseActive) {
      return false;
    }
    if (this.processStatus !== ProcessStatusEnumTypes.ACTIVE) {
      return false;
    }
    if (!user.canSolicitDevelopmentPlan) {
      return false;
    }
    return user.canSolicitDevelopmentPlan;
  }

  protected solicitDevelopmentPlan(user: MonitoringUserDevelopmentPlanSummaryExtended) {
    if (!user) return;
    if (!this.canSolicitDevelopmentPlan(user)) return;
    this.logger.info(`Solicit evaluation for user ${user.userId}`);
    this.onSolicitDevelopmentPlan.emit(user);
  }

  toggleActionColumn(user: MonitoringUserDevelopmentPlanSummaryExtended, event: Event): void {
    this.menuItems = [];

    // Azione: Impersona utente
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.impersonate.IMPERSONATE_USER),
      icon: "assets/icons/user.svg",
      disabled: !user?.canImpersonateUser,
      command: () => {
        this.onImpersonateUser(user);
      }
    });

    // Azione: Impersona manager
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.impersonate.IMPERSONATE_MANAGER),
      icon: "assets/icons/users.svg",
      disabled: !user?.canImpersonateManager,
      command: () => {
        this.onImpersonateManager(user);
      }
    });

    // Azione: Reapri valutazione
    /*this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.REOPEN_EVALUATION),
      icon: "assets/icons/wand.svg",
      disabled: !user?.canReopenDevelopmentPlan,
      command: () => {
        this.reopenDevelopmentPlan(user);
      }
    });*/

    // Azione: Sollecito valutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.SOLICIT_COMPILATION),
      icon: "assets/icons/mail.svg",
      disabled: !this.canSolicitDevelopmentPlan(user),
      command: () => {
        this.solicitDevelopmentPlan(user);
      }
    });

    if (user?.developmentPlanStatus === DevelopmentPlanTeamMemberStatusEnumTypes.COMPLETED) {
      // mostro il link alla pagina di dettaglio del piano di sviluppo
      this.menuItems.push({
        label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.VIEW_DEVELOPMENT_PLAN),
        icon: "assets/icons/eye.svg",
        command: () => {
          this.viewDevelopmentPlan(user);
        }
      });
    } else {
      // inserisco lo stesso l' elemento, ma lo disabilito
      this.menuItems.push({
        label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.VIEW_DEVELOPMENT_PLAN),
        icon: "assets/icons/eye.svg",
        disabled: true
      });
    }

    this.apeMenu.toggle(event);
  }

  protected viewDevelopmentPlan(user: MonitoringUserDevelopmentPlanSummaryExtended) {
    if (!user) return;
    if (!this.processId) return;
    if (!user.userId) return;
    this.logger.info(`View development plan for user ${user.userId} and process ${this.processId}`);
    // imposta in session il path corrente per il onBackClick
    // this.sessionService.set("currentPath", UrlCollection.adminMonitoringDevelopmentPlan);
    void this.router.navigate([
      UrlCollection.adminDevelopmentPlan, user.userId, this.processId
    ], { replaceUrl: true });
  }

  /**
   * Emette l'evento per impersonare l'utente
   * @param user L'utente da impersonare
   */
  private onImpersonateUser(user: MonitoringUserDevelopmentPlanSummaryExtended): void {
    if (!user) return;
    this.impersonateUser.emit(user);
  }

  /**
   * Emette l'evento per impersonare il manager
   * @param user L'utente il cui manager deve essere impersonato
   */
  private onImpersonateManager(user: MonitoringUserDevelopmentPlanSummaryExtended): void {
    if (!user) return;
    this.impersonateManager.emit(user);
  }
}
